import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Package,
  Loader2,
  Search,
  Pause,
  ClipboardCheck,
  AlertTriangle,
  CheckCircle2,
  Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import { toast } from 'sonner';
import {
  useInventoryCount,
  useInventoryCountItems,
  useInventoryCountsRealtime,
  useStartCount,
  useUpdateCountItem,
  useAddCountItem,
  useCompleteCount,
} from './useInventoryCounts';
import { formatDate } from './utils';
import type { ActiveTab, InventoryCountItem } from './types';

interface SelectedProduct {
  id: string;
  name: string;
  sku: string | null;
  image_url: string | null;
  barcode: string | null;
  /** The inventory_count_items row id, if this product is already in the count */
  countItemId: string | null;
}

export default function InventoryCountActive() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentLocation } = useLocationStore();

  useInventoryCountsRealtime();

  const { data: count, isLoading: countLoading } = useInventoryCount(id);
  const {
    data: items = [],
    isLoading: itemsLoading,
    refetch: refetchItems,
  } = useInventoryCountItems(id);

  const updateCountItem = useUpdateCountItem();
  const addCountItem = useAddCountItem();
  const completeCount = useCompleteCount();
  const startCount = useStartCount();

  // Auto-start the count if it is still scheduled when entering the active page
  useEffect(() => {
    if (count && count.status === 'scheduled') {
      startCount.mutateAsync(count.id).catch(() => {
        // Ignore -- may already be started by another user
      });
    }
  }, [count?.id, count?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── UI state ───────────────────────────────
  const [activeTab, setActiveTab] = useState<ActiveTab>('all');
  const [showDetails, setShowDetails] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [countQuantity, setCountQuantity] = useState('1');
  const [quickScanMode, setQuickScanMode] = useState(false);
  const [searchResults, setSearchResults] = useState<
    Array<{
      id: string;
      name: string;
      sku: string | null;
      image_url: string | null;
      barcode: string | null;
    }>
  >([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [recentlyCountedIds, setRecentlyCountedIds] = useState<Set<string>>(new Set());

  // The currently selected product (from clicking a row OR picking a search result)
  const [selectedProduct, setSelectedProduct] = useState<SelectedProduct | null>(null);

  // Track local counted quantities for items updated in this session
  const [localQuantities, setLocalQuantities] = useState<Record<string, number>>({});

  const searchInputRef = useRef<HTMLInputElement>(null);
  const quantityInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Derived data ───────────────────────────
  const itemsWithLocal = useMemo(() => {
    return items.map((item) => ({
      ...item,
      counted_quantity:
        localQuantities[item.id] !== undefined
          ? localQuantities[item.id]
          : item.counted_quantity,
    }));
  }, [items, localQuantities]);

  const tabCounts = useMemo(() => {
    const counted = itemsWithLocal.filter((item) => item.counted_quantity !== null).length;
    const uncounted = itemsWithLocal.filter((item) => item.counted_quantity === null).length;
    return { all: itemsWithLocal.length, counted, uncounted };
  }, [itemsWithLocal]);

  const filteredItems = useMemo(() => {
    let result = itemsWithLocal;
    switch (activeTab) {
      case 'counted':
        result = result.filter((item) => item.counted_quantity !== null);
        break;
      case 'uncounted':
        result = result.filter((item) => item.counted_quantity === null);
        break;
    }
    return result;
  }, [activeTab, itemsWithLocal]);

  // Product filter badges (for partial counts)
  const filterBadges = useMemo(() => {
    if (!count || count.count_type !== 'partial') return [];
    return items
      .filter((item) => item.product?.name)
      .slice(0, 8)
      .map((item) => ({
        id: item.product_id,
        name: item.product!.name,
      }));
  }, [count, items]);

  const tabs: { id: ActiveTab; label: string; count: number }[] = [
    { id: 'all', label: 'All', count: tabCounts.all },
    { id: 'counted', label: 'Counted', count: tabCounts.counted },
    { id: 'uncounted', label: 'Uncounted', count: tabCounts.uncounted },
  ];

  // ── Click a product row in the table ───────
  const handleProductRowClick = useCallback(
    (item: InventoryCountItem) => {
      if (!item.product) return;

      // Set the product as selected
      setSelectedProduct({
        id: item.product.id,
        name: item.product.name,
        sku: item.product.sku,
        image_url: item.product.image_url,
        barcode: item.product.barcode,
        countItemId: item.id,
      });

      // Show the product name in the search field
      setSearchQuery(item.product.name);

      // Clear any open search dropdown
      setSearchResults([]);
      setShowSearchResults(false);

      // Pre-fill quantity with current counted value or 1
      const currentCounted =
        localQuantities[item.id] !== undefined
          ? localQuantities[item.id]
          : item.counted_quantity;
      setCountQuantity(currentCounted !== null ? currentCounted.toString() : '1');

      // Focus the quantity input so user can immediately type
      setTimeout(() => {
        if (quantityInputRef.current) {
          quantityInputRef.current.focus();
          quantityInputRef.current.select();
        }
      }, 50);
    },
    [localQuantities]
  );

  // ── Search products ────────────────────────
  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);

      // Clear selected product when user types something new
      setSelectedProduct(null);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      if (!query.trim()) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      searchTimeoutRef.current = setTimeout(async () => {
        const locationId = count?.location_id || currentLocation?.id;
        if (!locationId) return;

        const { data, error } = await supabase
          .from('products')
          .select('id, name, sku, image_url, barcode')
          .eq('location_id', locationId)
          .or(`name.ilike.%${query}%,sku.ilike.%${query}%,barcode.ilike.%${query}%`)
          .limit(10);

        if (!error && data) {
          setSearchResults(data);
          setShowSearchResults(true);
        }
      }, 300);
    },
    [count?.location_id, currentLocation?.id]
  );

  // ── Select a product from search results ───
  const handleSelectSearchResult = useCallback(
    (product: {
      id: string;
      name: string;
      sku: string | null;
      image_url: string | null;
      barcode: string | null;
    }) => {
      // Find if this product already exists in count items
      const existingItem = items.find((item) => item.product_id === product.id);

      setSelectedProduct({
        ...product,
        countItemId: existingItem?.id || null,
      });

      setSearchQuery(product.name);
      setSearchResults([]);
      setShowSearchResults(false);

      // Pre-fill quantity
      if (existingItem) {
        const currentCounted =
          localQuantities[existingItem.id] !== undefined
            ? localQuantities[existingItem.id]
            : existingItem.counted_quantity;
        setCountQuantity(currentCounted !== null ? currentCounted.toString() : '1');
      } else {
        setCountQuantity('1');
      }

      // Focus quantity input
      setTimeout(() => {
        if (quantityInputRef.current) {
          quantityInputRef.current.focus();
          quantityInputRef.current.select();
        }
      }, 50);
    },
    [items, localQuantities]
  );

  // ── Count button: apply the quantity ───────
  const handleCountButtonClick = useCallback(async () => {
    if (!id || !selectedProduct) return;
    const qty = parseFloat(countQuantity);
    if (isNaN(qty)) return;

    try {
      if (selectedProduct.countItemId) {
        // Update existing item -- SET the quantity (not add)
        setLocalQuantities((prev) => ({
          ...prev,
          [selectedProduct.countItemId!]: qty,
        }));
        setRecentlyCountedIds((prev) => new Set(prev).add(selectedProduct.countItemId!));

        await updateCountItem.mutateAsync({
          itemId: selectedProduct.countItemId,
          countedQty: qty,
          countId: id,
        });
      } else {
        // Add unplanned product
        const result = await addCountItem.mutateAsync({
          countId: id,
          productId: selectedProduct.id,
          countedQuantity: qty,
        });

        if (result) {
          setLocalQuantities((prev) => ({ ...prev, [result.id]: qty }));
          setRecentlyCountedIds((prev) => new Set(prev).add(result.id));
          refetchItems();
        }
        toast.success(`Added ${selectedProduct.name}`, {
          description: 'This item was not part of your planned count.',
        });
      }

      // Reset
      setSearchQuery('');
      setSelectedProduct(null);
      setCountQuantity('1');

      if (quickScanMode && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update count';
      toast.error('Count error', { description: message });
    }
  }, [
    id,
    selectedProduct,
    countQuantity,
    quickScanMode,
    updateCountItem,
    addCountItem,
    refetchItems,
  ]);

  // ── Complete count ─────────────────────────
  const handleComplete = useCallback(async () => {
    if (!id || !count) return;

    const uncountedCount = itemsWithLocal.filter((i) => i.counted_quantity === null).length;
    if (uncountedCount > 0) {
      if (
        !confirm(
          `You have ${uncountedCount} uncounted item(s). Uncounted items will be recorded as 0. Continue?`
        )
      ) {
        return;
      }
    }

    const countQuantitiesMap: Record<string, string> = {};
    const varianceReasons: Record<string, string> = {};
    itemsWithLocal.forEach((item) => {
      const qty =
        localQuantities[item.id] !== undefined
          ? localQuantities[item.id]
          : item.counted_quantity ?? 0;
      countQuantitiesMap[item.id] = qty.toString();
    });

    try {
      await completeCount.mutateAsync({
        countId: id,
        items: itemsWithLocal,
        countQuantities: countQuantitiesMap,
        varianceReasons,
      });
      navigate(`/stock-control/counts/${id}`);
    } catch {
      // Error is already shown by the mutation's onError handler
    }
  }, [id, count, itemsWithLocal, localQuantities, completeCount, navigate]);

  // ── Quick scan: auto-submit on barcode scan ──
  useEffect(() => {
    if (!quickScanMode || !searchQuery.trim() || selectedProduct) return;

    const timeout = setTimeout(() => {
      if (searchResults.length === 1) {
        handleSelectSearchResult(searchResults[0]);
        // Auto-count in quick scan mode
        setTimeout(() => {
          const btn = document.getElementById('count-action-btn');
          if (btn) btn.click();
        }, 100);
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [quickScanMode, searchQuery, searchResults, selectedProduct, handleSelectSearchResult]);

  // ── Loading ────────────────────────────────
  if (countLoading || itemsLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading count...</span>
        </div>
      </div>
    );
  }

  if (!count) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Count not found.</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate('/stock-control/counts')}
        >
          Back to counts
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* ── Header ──────────────────────────── */}
      <div className="flex items-start gap-3 mb-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 mt-0.5 shrink-0"
          onClick={() => navigate(`/stock-control/counts/${count.id}`)}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-lg font-semibold truncate">
              {count.name || count.count_number}
            </h2>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-sm text-primary hover:underline whitespace-nowrap"
            >
              {showDetails ? 'Hide details' : 'Show details'}
            </button>
          </div>

          {showDetails && (
            <div className="flex items-center gap-6 mt-2 text-sm">
              <div>
                <span className="text-muted-foreground">Start</span>
                <p className="font-medium">
                  {count.started_at
                    ? formatDate(count.started_at)
                    : formatDate(count.scheduled_at)}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Outlet</span>
                <p className="font-medium">{count.location?.name}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Product filter badges ─────────── */}
      {filterBadges.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-4 ml-11">
          {filterBadges.map((badge) => (
            <Badge key={badge.id} variant="secondary" className="text-xs">
              Product: {badge.name}
            </Badge>
          ))}
        </div>
      )}

      {/* ── Info bar ──────────────────────── */}
      <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 mb-4 ml-11">
        <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
        <p className="text-sm text-blue-900 dark:text-blue-100">
          Count the inventory at this outlet to ensure your inventory is correct.
        </p>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/stock-control/counts/${count.id}`)}
          >
            <Pause className="h-3.5 w-3.5 mr-1.5" />
            Pause
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/stock-control/counts/${count.id}`)}
          >
            <ClipboardCheck className="h-3.5 w-3.5 mr-1.5" />
            Review
          </Button>
        </div>
      </div>

      {/* ── Search + Count bar ────────────── */}
      <div className="flex items-center gap-3 mb-4 ml-11 relative">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            placeholder="Type to search for products"
            className={cn(
              'pl-9',
              selectedProduct && 'border-primary ring-1 ring-primary/20'
            )}
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            onFocus={() => {
              if (searchResults.length > 0 && !selectedProduct) {
                setShowSearchResults(true);
              }
            }}
            onBlur={() => {
              setTimeout(() => setShowSearchResults(false), 200);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && searchResults.length === 1 && !selectedProduct) {
                handleSelectSearchResult(searchResults[0]);
              }
              if (e.key === 'Escape') {
                setSearchQuery('');
                setSelectedProduct(null);
                setSearchResults([]);
                setShowSearchResults(false);
              }
            }}
          />

          {/* Selected product indicator */}
          {selectedProduct && (
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedProduct(null);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground bg-muted px-2 py-0.5 rounded"
            >
              Clear
            </button>
          )}

          {/* Search results dropdown */}
          {showSearchResults && searchResults.length > 0 && !selectedProduct && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-background border rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
              {searchResults.map((product) => {
                const isInPlan = items.some((i) => i.product_id === product.id);
                return (
                  <button
                    key={product.id}
                    className="flex items-center gap-3 w-full px-3 py-2 hover:bg-muted/50 text-left transition-colors"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelectSearchResult(product);
                    }}
                  >
                    {product.image_url ? (
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-8 h-8 rounded object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                        <Package className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{product.name}</p>
                      {product.sku && (
                        <p className="text-xs text-muted-foreground">{product.sku}</p>
                      )}
                    </div>
                    {!isInPlan && (
                      <Badge
                        variant="outline"
                        className="text-xs text-amber-600 border-amber-300 shrink-0"
                      >
                        Not in plan
                      </Badge>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-sm text-muted-foreground whitespace-nowrap">Quantity</Label>
          <Input
            ref={quantityInputRef}
            type="number"
            min="0"
            step="1"
            className="w-20 text-center"
            value={countQuantity}
            onChange={(e) => setCountQuantity(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && selectedProduct) {
                e.preventDefault();
                handleCountButtonClick();
              }
            }}
          />
        </div>

        <Button
          id="count-action-btn"
          variant="blaze"
          disabled={!selectedProduct}
          onClick={handleCountButtonClick}
        >
          Count
        </Button>

        <div className="flex items-center gap-2 ml-2 pl-2 border-l">
          <Switch
            id="quick-scan"
            checked={quickScanMode}
            onCheckedChange={setQuickScanMode}
          />
          <Label htmlFor="quick-scan" className="text-sm whitespace-nowrap">
            Quick scan mode
          </Label>
        </div>
      </div>

      {/* ── Sub-tabs ──────────────────────── */}
      <div className="border-b mb-4 ml-11">
        <nav className="flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
              )}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </nav>
      </div>

      {/* ── Items table ───────────────────── */}
      <div className="ml-11">
        {filteredItems.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <p>
              {activeTab === 'uncounted'
                ? 'All items have been counted.'
                : activeTab === 'counted'
                  ? 'No items have been counted yet.'
                  : 'No items in this count.'}
            </p>
          </div>
        ) : (
          <div className="rounded-lg border overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">
                    Product
                  </th>
                  <th className="text-right text-sm font-medium text-muted-foreground px-4 py-3">
                    Expected
                  </th>
                  <th className="text-right text-sm font-medium text-muted-foreground px-4 py-3 w-32">
                    Counted
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredItems.map((item) => {
                  const isSelected = selectedProduct?.countItemId === item.id;
                  const isInPlan =
                    item.expected_quantity > 0 ||
                    !recentlyCountedIds.has(item.id) ||
                    items.some(
                      (orig) => orig.id === item.id && orig.expected_quantity > 0
                    );
                  const showWarning =
                    !isInPlan || item.is_unplanned === true;

                  return (
                    <tr
                      key={item.id}
                      className={cn(
                        'transition-colors cursor-pointer',
                        isSelected
                          ? 'bg-primary/10 ring-1 ring-inset ring-primary/20'
                          : recentlyCountedIds.has(item.id)
                            ? 'bg-primary/5 hover:bg-primary/10'
                            : showWarning
                              ? 'bg-amber-50/50 dark:bg-amber-950/10 hover:bg-amber-50 dark:hover:bg-amber-950/20'
                              : 'hover:bg-muted/50'
                      )}
                      onClick={() => handleProductRowClick(item)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {showWarning && (
                            <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                          )}
                          {item.product?.image_url ? (
                            <img
                              src={item.product.image_url}
                              alt={item.product.name}
                              className="w-8 h-8 rounded object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                              <Package className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-sm">
                              {item.product?.name || 'Unknown'}
                            </p>
                            <div className="flex items-center gap-1">
                              {item.product?.sku && (
                                <span className="text-xs text-muted-foreground">
                                  {item.product.sku}
                                </span>
                              )}
                              {showWarning && (
                                <span className="text-xs text-amber-600 dark:text-amber-400">
                                  This item was not part of your planned count
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-sm">
                        {item.expected_quantity}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 justify-end">
                          <span
                            className={cn(
                              'tabular-nums text-sm',
                              item.counted_quantity !== null
                                ? 'font-medium'
                                : 'text-muted-foreground'
                            )}
                          >
                            {item.counted_quantity !== null ? item.counted_quantity : '-'}
                          </span>
                          {item.counted_quantity !== null && (
                            <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Bottom action bar ─────────────── */}
      <div className="flex items-center justify-end gap-3 mt-6 ml-11 pt-4 border-t">
        <Button
          variant="outline"
          onClick={() => navigate(`/stock-control/counts/${count.id}`)}
        >
          Save & Review
        </Button>
        <Button
          variant="blaze"
          onClick={handleComplete}
          disabled={completeCount.isPending}
        >
          {completeCount.isPending && (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          )}
          Complete Count
        </Button>
      </div>
    </div>
  );
}
